import React, { useState } from 'react';
import {
	CloseOutlined,
	HourglassOutlined,
	LoadingOutlined,
	MessageOutlined,
	RollbackOutlined,
} from '@ant-design/icons';
import { stopDefaultEvents } from '../../utils/DataAccessUtils';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import RowChatInput from '../row_chat_input/RowChatInput';

const moment = require('moment');

interface RowNotePreviewProps {
	// Display data
	lastNote: string | null;
	lastNoteAddedBy: string | null;
	lastNoteAddedAt: string | null;
	isLastNoteUnread: boolean;
	isLastNoteAwaitingResponse: boolean;
	isLastNoteAwaitingResponseFromCurrentUser: boolean;
	isLastNoteResponsePending: boolean;
	lastNoteTaggedUsers?: any[];
	// RowChatInput dependencies
	sendNote: (entity: any) => Promise<any>;
	sendNotesBulk?: (entities: any[]) => Promise<any>;
	onMentionSearch: (search: string, cb: (results: any[]) => void) => void;
	currentUser: any;
	entityId: string | number;
	entityLabel?: string;
	customUploadRequest?: (args: any) => void;
	allowHeicUpload?: boolean;
	onWsSendNote?: (text?: string, noteType?: string) => void;
	onWsTyping?: () => void;
	// Callbacks
	onNoteClick: () => void;
	onNoteSent: () => void;
	// Optional dismiss note status (WO-specific)
	onDismissNoteStatus?: (e: any) => void;
	dismissing?: boolean;
}

const RowNotePreview: React.FC<RowNotePreviewProps> = ({
	lastNote,
	lastNoteAddedBy,
	lastNoteAddedAt,
	isLastNoteUnread,
	isLastNoteAwaitingResponse,
	isLastNoteAwaitingResponseFromCurrentUser,
	isLastNoteResponsePending,
	lastNoteTaggedUsers,
	sendNote,
	sendNotesBulk,
	onMentionSearch,
	currentUser,
	entityId,
	entityLabel,
	customUploadRequest,
	allowHeicUpload = false,
	onWsSendNote,
	onWsTyping,
	onNoteClick,
	onNoteSent,
	onDismissNoteStatus,
	dismissing = false,
}) => {
	const [replyOpen, setReplyOpen] = useState(false);

	const handleNoteSent = () => {
		setReplyOpen(false);
		onNoteSent();
	};

	const renderRowChatInput = () => (
		<div
			onClick={(e) => stopDefaultEvents(e)}
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					setReplyOpen(false);
				}
			}}
			className="w-full"
		>
			<RowChatInput
				sendNote={sendNote}
				sendNotesBulk={sendNotesBulk}
				onMentionSearch={onMentionSearch}
				currentUser={currentUser}
				entityId={entityId}
				entityLabel={entityLabel}
				customUploadRequest={customUploadRequest}
				allowHeicUpload={allowHeicUpload}
				onWsSendNote={onWsSendNote}
				onWsTyping={onWsTyping}
				autoFocus
				onNoteSent={handleNoteSent}
				onNotesPersisted={onNoteSent}
				onCancel={() => setReplyOpen(false)}
			/>
		</div>
	);

	if (!lastNote) {
		return (
			<div className="rowTertiary">
				{!replyOpen && (
					<div className="max-h-0 overflow-hidden opacity-0 transition-all duration-200 ease-out group-hover:max-h-8 group-hover:opacity-100">
						<div className="flex min-h-8 items-center rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 bg-grey-3 px-2 py-1">
							<span
								role="button"
								className="footnoteregular inline-flex cursor-pointer items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary"
								onClick={(e) => {
									stopDefaultEvents(e);
									setReplyOpen(true);
								}}
							>
								<MessageOutlined className="text-xs" />
								Send Note
							</span>
						</div>
					</div>
				)}
				{replyOpen && renderRowChatInput()}
			</div>
		);
	}

	const hasTaggedUsers = (lastNoteTaggedUsers || []).length > 0;
	const showDismissButton =
		onDismissNoteStatus &&
		isLastNoteAwaitingResponse &&
		!isLastNoteAwaitingResponseFromCurrentUser &&
		!hasTaggedUsers;

	const renderStatusIndicator = () => {
		if (isLastNoteAwaitingResponseFromCurrentUser) {
			return (
				<>
					<span className="relative inline-block">
						<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
							<HourglassOutlined className="text-sm" />
						</span>
						{isLastNoteUnread && (
							<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
						)}
					</span>
					<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
						Waiting on you:
					</span>
				</>
			);
		}

		if (isLastNoteAwaitingResponse && showDismissButton) {
			return (
				<span className="relative inline-flex items-center gap-1 whitespace-nowrap rounded-[2px] border border-solid border-transparent bg-transparent px-1 transition-colors group-hover/notePreviewBar:border-grey-5 group-hover/notePreviewBar:bg-white">
					<span className="relative inline-block">
						<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
							<HourglassOutlined className="text-sm" />
						</span>
						{isLastNoteUnread && (
							<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
						)}
					</span>
					<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
						Awaiting response
						<span className="opacity-100 group-hover/notePreviewBar:opacity-0">:</span>
					</span>
					<span
						role="button"
						className={`absolute -right-[7px] -top-[5px] h-[15px] w-[15px] cursor-pointer items-center justify-center rounded-full border border-solid border-grey-5 bg-white ${
							dismissing ? 'flex' : 'hidden group-hover/notePreviewBar:flex'
						}`}
						onClick={dismissing ? stopDefaultEvents : onDismissNoteStatus}
					>
						{dismissing ? (
							<LoadingOutlined
								className="!text-black"
								style={{ fontSize: 6, width: 6, height: 6 }}
							/>
						) : (
							<CloseOutlined className="!text-black" style={{ fontSize: 6, width: 6, height: 6 }} />
						)}
					</span>
				</span>
			);
		}

		if (isLastNoteAwaitingResponse) {
			return (
				<>
					<span className="relative inline-block">
						<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
							<HourglassOutlined className="text-sm" />
						</span>
						{isLastNoteUnread && (
							<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
						)}
					</span>
					<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
						Awaiting response:
					</span>
				</>
			);
		}

		return (
			<span className="relative inline-block">
				<MessageOutlined className=" text-grey-9" />
				{isLastNoteUnread && (
					<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
				)}
			</span>
		);
	};

	return (
		<div className="rowTertiary">
			<div
				className={`flex min-w-0 max-w-full flex-col items-start gap-1 overflow-hidden rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 px-2 py-1 ${
					replyOpen ? 'w-full bg-grey-4' : 'justify-center bg-grey-3'
				} `}
			>
				<div className="group/notePreviewBar w-full min-w-0 max-w-full">
					<div
						onClick={(e) => {
							stopDefaultEvents(e);
							onNoteClick();
						}}
						className="flex min-w-0 max-w-full cursor-pointer flex-row items-center gap-2"
					>
						<span className="relative flex shrink-0 items-center gap-1">
							{isLastNoteResponsePending ? (
								<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
									{'[>7d]'}
								</span>
							) : null}
							{renderStatusIndicator()}
						</span>
						<span
							className="footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
							title={lastNote}
						>
							{lastNote}
						</span>
						<span className="footnoteregular ml-1 shrink-0 text-grey-9">-</span>
						<span className="flex shrink-0 items-center">
							<UserNameWithEmail mode="avatar" email={lastNoteAddedBy} size="small" />
						</span>
						<span className="footnoteregular shrink-0 whitespace-nowrap text-grey-9">
							{' ∙ '}
							{moment(lastNoteAddedAt).format('hh:mm a')}
						</span>
						{!replyOpen && (
							<span
								role="button"
								className="footnoteregular ml-2 hidden shrink-0 cursor-pointer flex-row items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary group-hover/notePreviewBar:inline-flex"
								onClick={(e) => {
									stopDefaultEvents(e);
									setReplyOpen(true);
								}}
							>
								<RollbackOutlined className="text-xs" />
								Reply
							</span>
						)}
					</div>
				</div>
				{replyOpen && renderRowChatInput()}
			</div>
		</div>
	);
};

export default RowNotePreview;
